import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
  FiPieChart, FiSearch, FiX, FiPlus, FiCopy, FiFilePlus, FiUpload, FiSave, FiTrash2, FiRefreshCw, FiArrowUp, FiArrowDown,
} from 'react-icons/fi';
import AppModal from '../common/AppModal';
import SQLEditor from '../SQLEditor';
import { useConfirm } from '../ConfirmDialog';
import { usePrompt } from '../PromptDialog';
import { formatApiError } from '../../utils/formatters';
import {
  buildAnalysisSql, FILTER_MODES_BY_TYPE, DATA_TYPES, AGG_FUNCTIONS, ANALYTIC_FUNCTIONS, TEMPLATES,
  analyticTakesCol, analyticTakesN, supportsHints, ANALYSIS_DB_TYPES,
} from '../../utils/analysisQueryBuilder';
import { defaultSpec, withDefaults, syncColumns, canWrite, SPEC_VERSION } from '../../utils/analysisDefinition';
import * as api from '../../services/dataAnalysis';
import { listenDrag } from '../../utils/dragListen';
import { toCssPx } from '../../utils/appZoom';
import memoWindow from '../common/memoWindow';
import EditorConnectionChip from '../common/EditorConnectionChip';
import { storedTime } from '../../utils/storedTime';
import '../common/Workflow.css';
import './DataAnalysis.css';

/**
 * 데이터 분석(2026-10-28 개편 — docs/features/30-data-analysis.md) — 분석 SQL 을 **만들어 편집기에 넣는다**.
 *
 * 예전에는 결과 탭 옆 오른쪽 패널이었고 「분석 조회」 로 직접 실행했다. 지금은 SQL 빌더처럼 편집기 자리의 작업 탭이고
 * 실행하지 않는다(실행은 편집기에서 — 두 번째 실행 경로를 만들지 않는다). 왼쪽은 설정, 오른쪽은 만들어지는 SQL 의 미리보기다.
 *  · 분석 대상 — 테이블(스키마·테이블을 고르면 컬럼과 주석을 읽는다) 또는 결과 탭의 쿼리(예전 방식)
 *  · 분석 유형 — 직접 구성(컬럼·필터·그룹·정렬·분석 함수·PIVOT) 또는 템플릿(UNIQUE 건수 · Top N · 중복 건 · 최종/최초 건)
 *  · 정의 저장·불러오기 — 서버에 사용자별(services/dataAnalysis). 불러온 정의의 모르는 칸은 그대로 다시 저장한다(utils/analysisDefinition)
 * SQL 은 utils/analysisQueryBuilder 한 곳에서 만든다 — 화면에서 SQL 글을 조립하지 말 것.
 */

const CUSTOM_TABS = ['columns', 'filter', 'group', 'sort', 'analytic', 'pivot'];
const TEMPLATE_TABS = ['template', 'columns', 'filter'];
const ROW_CHOICES = [0, 50, 100, 500, 1000, 5000, 10000];
const LEFT_W_KEY = 'livesql.dataAnalysis.leftWidth';
const LEFT_MIN = 360;
const readLeftWidth = () => {
  try { const v = Number(localStorage.getItem(LEFT_W_KEY)); return v >= LEFT_MIN ? v : 560; } catch { return 560; }
};
const NOOP = () => {};
const splitTable = (full) => {
  const s = String(full || '');
  const i = s.indexOf('.');
  return i > 0 ? { owner: s.slice(0, i), name: s.slice(i + 1) } : (s ? { owner: '', name: s } : null);
};

/** 고른 컬럼들 — 칩으로 보이고 아래 목록에서 더한다. withDir 이면 칩마다 ASC/DESC 를 누른다 */
function ColumnChips({ value, onChange, columns, withDir }) {
  const { t } = useTranslation();
  const items = (value || []).map(v => (typeof v === 'string' ? { col: v } : v));
  const chosen = new Set(items.map(i => i.col));
  const emit = (next) => onChange(withDir ? next : next.map(i => i.col));
  return (
    <div className="dan-chips">
      {items.map((it, i) => (
        <span key={it.col} className="dan-chip">
          {it.col}
          {withDir && (
            <button type="button" className="dan-chip-dir" title={t('common:dan.toggleDir')}
              onClick={() => emit(items.map((x, j) => (j === i ? { ...x, dir: x.dir === 'DESC' ? 'ASC' : 'DESC' } : x)))}>
              {it.dir === 'DESC' ? 'DESC' : 'ASC'}
            </button>
          )}
          <button type="button" aria-label={t('common:ui.delete')} onClick={() => emit(items.filter((_, j) => j !== i))}><FiX size={10} /></button>
        </span>
      ))}
      <select className="dan-chip-add" value="" onChange={(e) => { if (e.target.value) emit([...items, { col: e.target.value, dir: 'ASC' }]); }}>
        <option value="">{t('common:dan.addColumn')}</option>
        {columns.filter(c => !chosen.has(c.name)).map(c => <option key={c.name} value={c.name}>{c.name}{c.comment ? ` — ${c.comment}` : ''}</option>)}
      </select>
    </div>
  );
}
ColumnChips.propTypes = { value: PropTypes.array, onChange: PropTypes.func.isRequired, columns: PropTypes.array.isRequired, withDir: PropTypes.bool };

/** 한 컬럼 고르기(select) */
function ColumnSelect({ value, onChange, columns, allowEmpty, emptyLabel }) {
  const { t } = useTranslation();
  return (
    <select value={value || ''} onChange={e => onChange(e.target.value)}>
      {(allowEmpty || !value) && <option value="">{emptyLabel || t('common:dan.pickColumn')}</option>}
      {value && !columns.some(c => c.name === value) && <option value={value}>{value}</option>}
      {columns.map(c => <option key={c.name} value={c.name}>{c.name}{c.comment ? ` — ${c.comment}` : ''}</option>)}
    </select>
  );
}
ColumnSelect.propTypes = { value: PropTypes.string, onChange: PropTypes.func.isRequired, columns: PropTypes.array.isRequired, allowEmpty: PropTypes.bool, emptyLabel: PropTypes.string };

/** 분석 대상 테이블 — 스키마를 고르고 이름·주석으로 찾는다(카탈로그는 서버가 읽는다: 이름·주석만) */
function TablePicker({ connectionId, schema, table, onSchema, onPick }) {
  const { t } = useTranslation();
  const [schemas, setSchemas] = useState([]);
  const [q, setQ] = useState('');
  const [found, setFound] = useState(null);
  const [busy, setBusy] = useState(false);
  const tRef = useRef(t);
  tRef.current = t;
  useEffect(() => {
    setFound(null);
    if (!connectionId) { setSchemas([]); return undefined; }
    let alive = true;
    api.lookupCatalog({ connectionId }).then((r) => {
      if (!alive) return;
      const list = r.schemas || [];
      setSchemas(list);
      // 연결의 기본 스키마는 대소문자가 목록과 다를 수 있다(접속 계정 이름 그대로) — 목록의 이름으로 맞춘다
      const def = String(r.defaultSchema || '');
      if (!schema && def) onSchema(list.find(s => s.toUpperCase() === def.toUpperCase()) || def);
    }).catch(err => alive && toast.error(formatApiError(err, tRef.current)));
    return () => { alive = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- 연결이 바뀔 때만 스키마 목록을 다시 읽는다
  }, [connectionId]);
  const search = async (listAll) => {
    if (!connectionId) return;
    setBusy(true);
    try { setFound((await api.lookupCatalog({ connectionId, schema: schema || null, q: listAll ? '' : q })).tables || []); } catch (err) { toast.error(formatApiError(err, t)); } finally { setBusy(false); }
  };
  if (!connectionId) return <div className="dan-note">{t('common:dan.noConnection')}</div>;
  return (
    <div className="dan-picker">
      <div className="dan-row">
        <select value={schema || ''} onChange={e => { onSchema(e.target.value); setFound(null); }} title={t('common:dan.schema')}>
          <option value="">{t('common:dan.schemaAny')}</option>
          {schema && !schemas.includes(schema) && <option value={schema}>{schema}</option>}
          {schemas.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <input className="dan-grow" value={q} onChange={e => setQ(e.target.value)} placeholder={t('common:dan.tableSearchPh')}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); if (q.trim()) search(false); } }} />
        <button type="button" className="app-btn" onClick={() => search(false)} disabled={busy || !q.trim()}><FiSearch size={12} /> {t('common:dan.find')}</button>
        <button type="button" className="app-btn" onClick={() => search(true)} disabled={busy || !schema} title={t('common:dan.listTablesHint')}>{t('common:dan.listTables')}</button>
      </div>
      {found && (
        <ul className="dan-found">
          {found.map((f) => {
            const name = `${f.owner}.${f.name}`;
            return (
              <li key={name} className={name === table ? 'on' : ''} onClick={() => { onPick(name); setFound(null); }}>
                <b className="mono">{name}</b>{f.type === 'VIEW' ? <span className="dan-note"> VIEW</span> : null}
                {f.comment ? <span className="dan-note"> — {f.comment}</span> : null}
              </li>
            );
          })}
          {!found.length && <li className="dan-note">{t('common:dan.noTables')}</li>}
        </ul>
      )}
    </div>
  );
}
TablePicker.propTypes = { connectionId: PropTypes.string, schema: PropTypes.string, table: PropTypes.string, onSchema: PropTypes.func.isRequired, onPick: PropTypes.func.isRequired };

// connectionId 는 SQL 편집 탭의 연결이다 — 이 화면은 고르지 않는다(만든 SQL 이 그 탭의 세션에서 실행된다, 제목줄 칩은 표시만)
function DataAnalysisModal({ open, onClose, connections, connectionId, dbType, resultSource, onInsert, onNewTab }) {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const prompt = usePrompt();
  const [spec, setSpec] = useState(() => withDefaults(defaultSpec()));
  const [tab, setTab] = useState('columns');
  const [nameFilter, setNameFilter] = useState('');
  const [sortByName, setSortByName] = useState(null);
  const [hideHidden, setHideHidden] = useState(false);
  const [colState, setColState] = useState({ loading: false, error: null, comment: '' });
  const [defs, setDefs] = useState([]);
  const [cur, setCur] = useState(null);   // 불러온·저장한 정의 { id, name, writable }
  const [defQ, setDefQ] = useState('');   // 왼쪽 목록 거르기(이름·대상)
  const [leftWidth, setLeftWidth] = useState(readLeftWidth);
  const [splitDrag, setSplitDrag] = useState(false);
  const bodyRef = useRef(null);
  const tRef = useRef(t);
  tRef.current = t;

  const patch = useCallback((p) => setSpec(s => ({ ...s, ...p })), []);
  const patchSource = (p) => setSpec(s => ({ ...s, source: { ...s.source, ...p } }));
  const patchParams = (key, p) => setSpec(s => ({ ...s, templateParams: { ...s.templateParams, [key]: { ...(s.templateParams[key] || {}), ...p } } }));
  const patchPivot = (p) => setSpec(s => ({ ...s, pivot: { ...s.pivot, ...p } }));
  const columns = spec.columns;
  const updateCol = (name, p) => patch({ columns: columns.map(c => (c.name === name ? { ...c, ...p } : c)) });

  // 처음 열 때 — 결과 탭이 있으면 그것을, 없으면 테이블을 대상으로
  const openedOnce = useRef(false);
  useEffect(() => {
    if (!open || openedOnce.current) return;
    openedOnce.current = true;
    if (resultSource && resultSource.sql) readResult();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // 저장된 정의 목록
  const reloadDefs = useCallback(() => {
    api.listDefs().then(setDefs).catch(err => toast.error(formatApiError(err, tRef.current)));
  }, []);
  useEffect(() => { if (open) reloadDefs(); }, [open, reloadDefs]);

  // SQL 편집 탭의 연결이 바뀌면 고른 스키마·테이블은 앞 연결의 것이다 — 비운다(처음 정해질 때는 그대로)
  const prevConn = useRef(connectionId);
  useEffect(() => {
    const before = prevConn.current;
    prevConn.current = connectionId;
    if (before && connectionId && before !== connectionId) setSpec(s => (s.source.kind === 'table' ? { ...s, source: { ...s.source, schema: '', table: '' } } : s));
  }, [connectionId]);

  // 테이블이 대상이면 그 컬럼(주석 포함)을 읽어 설정을 맞춘다 — 같은 이름 컬럼의 설정은 남는다
  const tableKey = spec.source.kind === 'table' && spec.source.table && connectionId ? `${connectionId}|${spec.source.table}` : '';
  useEffect(() => {
    if (!tableKey) return undefined;
    let alive = true;
    setColState({ loading: true, error: null, comment: '' });
    api.describeTable({ connectionId, table: spec.source.table }).then((tbl) => {
      if (!alive) return;
      setSpec(s => ({ ...s, columns: syncColumns(s.columns, (tbl.columns || []).map(c => ({ name: c.name, type: c.type, comment: c.comment }))) }));
      setColState({ loading: false, error: null, comment: tbl.comment || '' });
    }).catch((err) => { if (alive) setColState({ loading: false, error: formatApiError(err, tRef.current), comment: '' }); });
    return () => { alive = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- 대상 테이블·연결이 바뀔 때만 읽는다
  }, [tableKey]);

  /** 결과 탭의 쿼리를 대상으로 — 지금 결과 탭의 SQL·컬럼을 읽어 둔다(정의에 함께 저장돼 결과 탭이 없어도 다시 연다) */
  function readResult() {
    if (!resultSource || !resultSource.sql) { toast.info(t('common:dan.noResult')); return; }
    const metas = (resultSource.columns || []).map(m => (typeof m === 'string' ? { name: m } : m)).filter(m => m && m.name);
    setSpec(s => ({
      ...s,
      source: { ...s.source, kind: 'result', sql: resultSource.sql, resultName: resultSource.name || '', columnNames: metas.map(m => m.name) },
      columns: syncColumns(s.source.kind === 'result' ? s.columns : [], metas, resultSource.sampleRow),
    }));
  }

  const switchKind = (kind) => {
    if (kind === spec.source.kind) return;
    if (kind === 'result') { readResult(); return; }
    setSpec(s => ({ ...s, source: { ...s.source, kind: 'table' }, columns: [] }));
    setColState({ loading: false, error: null, comment: '' });
  };

  // 만들어지는 SQL — 고칠 때마다 다시
  const db = String(dbType || 'oracle').toLowerCase();
  const built = useMemo(() => {
    const src = spec.source;
    if (src.kind === 'table' && !src.table) return { sql: '', hint: t('common:dan.pickTableFirst') };
    if (src.kind === 'result' && !src.sql) return { sql: '', hint: t('common:dan.noResult') };
    if (!ANALYSIS_DB_TYPES.includes(db)) return { sql: '', error: t('common:aqb.unsupportedDb', { dbType: db }) };
    try {
      const sql = buildAnalysisSql(src.kind === 'result' ? src.sql : null, spec, {
        dbType: db, table: src.kind === 'table' ? splitTable(src.table) : null, sourceColumns: src.columnNames,
      });
      return { sql };
    } catch (err) {
      return { sql: '', error: err.message || String(err) };
    }
  }, [spec, db, t]);

  // ── 정의 저장 · 불러오기 · 지우기 ──
  const saveDef = async () => {
    if (cur && !cur.writable) { toast.warn(t('common:dan.newerReadOnly')); return; }
    const name = await prompt({ title: t('common:dan.saveTitle'), message: t('common:dan.saveBody'), defaultValue: cur ? cur.name : (spec.source.table || spec.source.resultName || ''), confirmText: t('common:action.save') });
    if (!name || !name.trim()) return;
    try {
      const overwrite = cur && cur.name === name.trim();
      const body = { name: name.trim(), spec: { ...spec, version: spec.version || SPEC_VERSION } };
      const def = overwrite ? await api.updateDef(cur.id, body) : await api.createDef(body);
      setCur({ id: def.id, name: def.name, writable: true });
      reloadDefs();
      toast.success(t(overwrite ? 'common:dan.updated' : 'common:dan.saved', { name: def.name }));
    } catch (err) { toast.error(formatApiError(err, t)); }
  };
  const loadDef = async (id) => {
    if (!id) return;
    try {
      const def = await api.getDef(id);
      const next = withDefaults(def.spec);
      setSpec(next);
      const writable = canWrite(def.spec);
      setCur({ id: def.id, name: def.name, writable });
      setTab(next.template === 'custom' ? 'columns' : 'template');
      if (!writable) toast.warn(t('common:dan.newerReadOnly'));
    } catch (err) { toast.error(formatApiError(err, t)); }
  };
  const removeDef = async () => {
    if (!cur) return;
    const ok = await confirm({ title: t('common:dan.deleteTitle'), message: t('common:dan.deleteBody', { name: cur.name }), confirmText: t('common:ui.delete'), variant: 'danger' });
    if (!ok) return;
    try { await api.deleteDef(cur.id); setCur(null); reloadDefs(); } catch (err) { toast.error(formatApiError(err, t)); }
  };
  const visibleDefs = useMemo(() => {
    const k = defQ.trim().toLowerCase();
    return k ? defs.filter(d => `${d.name} ${d.target || ''}`.toLowerCase().includes(k)) : defs;
  }, [defs, defQ]);
  const startNew = () => { setSpec(withDefaults(defaultSpec())); setCur(null); setTab('columns'); setColState({ loading: false, error: null, comment: '' }); };

  const copy = async () => {
    try { await navigator.clipboard.writeText(built.sql); toast.success(t('common:dan.copied')); } catch { toast.error(t('common:dan.copyFailed')); }
  };

  // 왼쪽 폭 끌기 — 분리 창에서도 그 창의 문서로(utils/dragListen), 마우스 거리는 화면 px 라 CSS px 로 바꾼다
  const startSplit = (e) => {
    e.preventDefault();
    const x0 = e.clientX;
    const w0 = leftWidth;
    const max = bodyRef.current ? Math.max(LEFT_MIN, toCssPx(bodyRef.current.getBoundingClientRect().width) - 240) : 1000;
    let w = w0;
    setSplitDrag(true);
    listenDrag(e, (ev) => {
      w = Math.round(Math.min(max, Math.max(LEFT_MIN, w0 + toCssPx(ev.clientX - x0))));
      setLeftWidth(w);
    }, () => {
      setSplitDrag(false);
      try { localStorage.setItem(LEFT_W_KEY, String(w)); } catch { /* 저장 못 해도 이번 창에는 남는다 */ }
    });
  };

  // ── 목록 거르기 ──
  const nameFiltered = useMemo(() => {
    const q = nameFilter.trim().toLowerCase();
    const filtered = q ? columns.filter(c => c.name.toLowerCase().includes(q) || String(c.comment || '').toLowerCase().includes(q)) : columns;
    if (!sortByName) return filtered;
    const dir = sortByName === 'desc' ? -1 : 1;
    return filtered.slice().sort((a, b) => dir * String(a.name).localeCompare(String(b.name)));
  }, [columns, nameFilter, sortByName]);

  const isTemplate = spec.template !== 'custom';
  const tabs = isTemplate ? TEMPLATE_TABS : CUSTOM_TABS;
  const activeTab = tabs.includes(tab) ? tab : tabs[0];
  const hints = supportsHints(db);

  const colKeys = isTemplate ? ['filter'] : ['show', 'filter', 'group', 'sort'];
  const colLabel = (key) => t(`common:ap.${key}`);
  const allChecked = (key) => columns.length > 0 && (key === 'show' ? columns.every(c => !c.hide) : columns.every(c => !!c[key]));
  const someChecked = (key) => (key === 'show' ? columns.some(c => !c.hide) : columns.some(c => !!c[key])) && !allChecked(key);
  const toggleAll = (key, on) => patch({ columns: columns.map(c => (key === 'show' ? { ...c, hide: !on } : { ...c, [key]: on })) });

  const colCell = (c) => (
    <td className="dan-colname" title={[c.name, c.type, c.comment].filter(Boolean).join(' · ')}>
      <span className="dan-name">{c.name}</span>
      {c.comment ? <span className="dan-cmt">{c.comment}</span> : null}
    </td>
  );

  const renderColumns = () => (
    <>
      <div className="dan-subbar">
        <input type="search" className="dan-grow" value={nameFilter} onChange={e => setNameFilter(e.target.value)} placeholder={t('common:dan.searchColumns')} />
        {!isTemplate && (
          <label className="dan-check"><input type="checkbox" checked={hideHidden} onChange={e => setHideHidden(e.target.checked)} /> {t('common:ap.hideHidden')}</label>
        )}
        <span className="dan-note">{t('common:ap.visibleCount', { visible: nameFiltered.filter(c => !hideHidden || !c.hide).length, total: columns.length })}</span>
      </div>
      <table className="dan-grid">
        <thead>
          <tr>
            <th className="dan-colname">
              <button type="button" className="dan-sortbtn" onClick={() => setSortByName(s => (s === null ? 'asc' : s === 'asc' ? 'desc' : null))}
                title={sortByName === 'asc' ? t('common:ap.sortedAsc') : sortByName === 'desc' ? t('common:ap.sortedDesc') : t('common:ap.sortAscHint')}>
                {t('common:ap.column')} {sortByName === 'asc' ? <FiArrowUp size={11} /> : sortByName === 'desc' ? <FiArrowDown size={11} /> : null}
              </button>
            </th>
            {colKeys.map(key => (
              <th key={key} className="dan-chk">
                <span>{colLabel(key)}</span>
                <input type="checkbox" checked={allChecked(key)} ref={el => { if (el) el.indeterminate = someChecked(key); }}
                  onChange={e => toggleAll(key, e.target.checked)} title={t('common:ap.toggleAll', { label: colLabel(key) })} />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {nameFiltered.filter(c => isTemplate || !hideHidden || !c.hide).map(c => (
            <tr key={c.name} className={!isTemplate && c.hide ? 'dan-row-hidden' : ''}>
              {colCell(c)}
              {colKeys.map(key => (
                <td key={key} className="dan-chk">
                  <input type="checkbox" checked={key === 'show' ? !c.hide : !!c[key]}
                    onChange={e => updateCol(c.name, key === 'show' ? { hide: !e.target.checked } : { [key]: e.target.checked })} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );

  const renderFilter = () => {
    const list = columns.filter(c => c.filter);
    if (!list.length) return <div className="dan-empty">{t('common:ap.noneFilter')}</div>;
    return (
      <div className="dan-cards">
        {list.map((c) => {
          const modes = FILTER_MODES_BY_TYPE[c.dataType] || FILTER_MODES_BY_TYPE.string;
          const isBetween = c.filterMode === 'between' || c.filterMode === 'date_between';
          const inputType = c.dataType === 'number' ? 'number' : c.dataType === 'date' ? 'date' : 'text';
          return (
            <div key={c.name} className="dan-card">
              <div className="dan-card-head">
                <span className="dan-name" title={c.name}>{c.name}</span>
                {c.comment ? <span className="dan-cmt">{c.comment}</span> : null}
                <select className="dan-push" value={c.dataType || 'string'} title={t('common:ap.dataType')}
                  onChange={e => { const dt = e.target.value; updateCol(c.name, { dataType: dt, filterMode: dt === 'number' ? 'eq' : dt === 'date' ? 'date_eq' : 'both%', filterValue: '', filterValue2: '' }); }}>
                  {DATA_TYPES.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div className="dan-row">
                <select value={c.filterMode} onChange={e => updateCol(c.name, { filterMode: e.target.value })}>
                  {modes.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                <input type={inputType} className="dan-grow" value={c.filterValue || ''} placeholder={isBetween ? t('common:ap.from') : t('common:ap.value')}
                  onChange={e => updateCol(c.name, { filterValue: e.target.value })} />
                {isBetween && (
                  <>
                    <span>~</span>
                    <input type={inputType} className="dan-grow" value={c.filterValue2 || ''} placeholder={t('common:ap.to')} onChange={e => updateCol(c.name, { filterValue2: e.target.value })} />
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderGroup = () => {
    const list = nameFiltered.filter(c => !c.group && !c.hide);
    if (!list.length) return <div className="dan-empty">{t('common:ap.noGroupDetail')}</div>;
    return (
      <table className="dan-grid dan-grid-wide">
        <thead><tr><th className="dan-colname">{t('common:ap.column')}</th><th>{t('common:ap.aggregate')}</th><th>{t('common:ap.alias')}</th></tr></thead>
        <tbody>
          {list.map(c => (
            <tr key={c.name}>
              {colCell(c)}
              <td className="dan-agg">
                <select value={c.aggFn || 'NONE'} onChange={e => updateCol(c.name, { aggFn: e.target.value })}>
                  {AGG_FUNCTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </td>
              <td className="dan-alias">
                <input type="text" value={c.alias || ''} onChange={e => updateCol(c.name, { alias: e.target.value })}
                  placeholder={(c.aggFn && c.aggFn !== 'NONE') ? `${c.aggFn === 'COUNT_DISTINCT' ? 'DIST_CNT' : c.aggFn}_${c.name}` : t('common:ap.optional')} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  const renderSort = () => {
    const list = nameFiltered.filter(c => c.sort);
    if (!list.length) return <div className="dan-empty">{t('common:ap.noneSort')}</div>;
    return (
      <table className="dan-grid dan-grid-wide">
        <thead><tr><th className="dan-colname">{t('common:ap.column')}</th><th>{t('common:ap.direction')}</th><th>{t('common:ap.priority')}</th></tr></thead>
        <tbody>
          {list.map(c => (
            <tr key={c.name}>
              {colCell(c)}
              <td className="dan-agg">
                <select value={c.sortDir || 'ASC'} onChange={e => updateCol(c.name, { sortDir: e.target.value })}>
                  <option value="ASC">{t('common:ap.asc')}</option>
                  <option value="DESC">{t('common:ap.desc')}</option>
                </select>
              </td>
              <td><input type="number" min={1} step={1} value={c.sortPriority || 1} onChange={e => updateCol(c.name, { sortPriority: Number(e.target.value) || 1 })} style={{ width: 70 }} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  const setAnalytic = (i, p) => patch({ analytics: spec.analytics.map((a, j) => (j === i ? { ...a, ...p } : a)) });
  const renderAnalytic = () => (
    <div className="dan-cards">
      <div className="dan-note">{t('common:dan.analyticHint')}</div>
      {spec.analytics.map((a, i) => (
        // eslint-disable-next-line react/no-array-index-key -- 항목에 고유 id 가 없고 순서가 곧 정체다
        <div key={i} className="dan-card">
          <div className="dan-card-head">
            <select value={a.fn} onChange={e => setAnalytic(i, { fn: e.target.value })}>
              {ANALYTIC_FUNCTIONS.map(fn => <option key={fn} value={fn}>{fn}</option>)}
            </select>
            {analyticTakesCol(a.fn) && (
              <ColumnSelect value={a.col} onChange={v => setAnalytic(i, { col: v })} columns={columns} allowEmpty={a.fn === 'COUNT'} emptyLabel={a.fn === 'COUNT' ? '*' : undefined} />
            )}
            {analyticTakesN(a.fn) && (
              <label className="dan-inline">{a.fn === 'NTILE' ? t('common:dan.buckets') : t('common:dan.offset')}
                <input type="number" min={1} value={a.n || ''} placeholder={a.fn === 'NTILE' ? '4' : '1'} onChange={e => setAnalytic(i, { n: e.target.value })} style={{ width: 56 }} />
              </label>
            )}
            <input className="dan-alias-in" value={a.alias || ''} onChange={e => setAnalytic(i, { alias: e.target.value })} placeholder={t('common:ap.alias')} />
            <button type="button" className="app-btn dan-push" onClick={() => patch({ analytics: spec.analytics.filter((_, j) => j !== i) })} aria-label={t('common:ui.delete')}><FiX size={12} /></button>
          </div>
          <div className="dan-field"><span>PARTITION BY</span><ColumnChips value={a.partition} onChange={v => setAnalytic(i, { partition: v })} columns={columns} /></div>
          <div className="dan-field"><span>ORDER BY</span><ColumnChips value={a.order} onChange={v => setAnalytic(i, { order: v })} columns={columns} withDir /></div>
        </div>
      ))}
      <button type="button" className="app-btn" onClick={() => patch({ analytics: [...spec.analytics, { fn: 'ROW_NUMBER', partition: [], order: [], alias: '' }] })}>
        <FiPlus size={12} /> {t('common:dan.addAnalytic')}
      </button>
    </div>
  );

  const pv = spec.pivot;
  const renderPivot = () => (
    <div className="dan-cards">
      <div className="dan-seg" role="radiogroup" aria-label={t('common:dan.tabs.pivot')}>
        {['none', 'pivot', 'unpivot'].map(m => (
          <button key={m} type="button" role="radio" aria-checked={pv.mode === m} className={pv.mode === m ? 'on' : ''} onClick={() => patchPivot({ mode: m })}>{t(`common:dan.pivotMode.${m}`)}</button>
        ))}
      </div>
      {pv.mode !== 'none' && <div className="dan-note">{t(pv.mode === 'pivot' ? 'common:dan.pivotHint' : 'common:dan.unpivotHint')}</div>}
      {pv.mode === 'pivot' && (
        <div className="dan-card">
          <div className="dan-field"><span>{t('common:dan.pivotCol')}</span><ColumnSelect value={pv.pivotCol} onChange={v => patchPivot({ pivotCol: v })} columns={columns} /></div>
          <div className="dan-field"><span>{t('common:dan.pivotValues')}</span>
            <input className="dan-grow" value={pv.values} onChange={e => patchPivot({ values: e.target.value })} placeholder={t('common:dan.pivotValuesPh')} />
          </div>
          <div className="dan-field"><span>{t('common:dan.pivotAgg')}</span>
            <div className="dan-row">
              <select value={pv.aggFn} onChange={e => patchPivot({ aggFn: e.target.value })}>
                {['SUM', 'COUNT', 'AVG', 'MIN', 'MAX'].map(fn => <option key={fn} value={fn}>{fn}</option>)}
              </select>
              <ColumnSelect value={pv.valueCol} onChange={v => patchPivot({ valueCol: v })} columns={columns} />
            </div>
          </div>
        </div>
      )}
      {pv.mode === 'unpivot' && (
        <div className="dan-card">
          <div className="dan-field"><span>{t('common:dan.unpivotCols')}</span><ColumnChips value={pv.unpivotCols} onChange={v => patchPivot({ unpivotCols: v })} columns={columns} /></div>
          <div className="dan-field"><span>{t('common:dan.nameAlias')}</span><input value={pv.nameAlias} onChange={e => patchPivot({ nameAlias: e.target.value })} /></div>
          <div className="dan-field"><span>{t('common:dan.valueAlias')}</span><input value={pv.valueAlias} onChange={e => patchPivot({ valueAlias: e.target.value })} /></div>
          <label className="dan-check"><input type="checkbox" checked={!!pv.includeNulls} onChange={e => patchPivot({ includeNulls: e.target.checked })} /> {t('common:dan.includeNulls')}</label>
        </div>
      )}
    </div>
  );

  const tp = spec.templateParams;
  const renderTemplate = () => (
    <div className="dan-cards">
      <div className="dan-note">{t(`common:dan.tplDesc.${spec.template}`)}</div>
      <div className="dan-card">
        {spec.template === 'unique' && (
          <div className="dan-field"><span>{t('common:dan.tplCols.unique')}</span><ColumnChips value={tp.unique.cols} onChange={v => patchParams('unique', { cols: v })} columns={columns} /></div>
        )}
        {spec.template === 'topn' && (
          <>
            <div className="dan-field"><span>{t('common:dan.tplCols.topn')}</span><ColumnChips value={tp.topn.cols} onChange={v => patchParams('topn', { cols: v })} columns={columns} /></div>
            <div className="dan-field"><span>{t('common:dan.topnBy')}</span>
              <div className="dan-row">
                <select value={tp.topn.by} onChange={e => patchParams('topn', { by: e.target.value })}>
                  <option value="count">{t('common:dan.topnByCount')}</option>
                  <option value="value">{t('common:dan.topnByValue')}</option>
                </select>
                <select value={tp.topn.dir} onChange={e => patchParams('topn', { dir: e.target.value })}>
                  <option value="DESC">{t('common:dan.topnDesc')}</option>
                  <option value="ASC">{t('common:dan.topnAsc')}</option>
                </select>
                <label className="dan-inline">N <input type="number" min={1} value={tp.topn.n} onChange={e => patchParams('topn', { n: e.target.value })} style={{ width: 64 }} /></label>
              </div>
            </div>
          </>
        )}
        {spec.template === 'dup' && (
          <div className="dan-field"><span>{t('common:dan.tplCols.dup')}</span><ColumnChips value={tp.dup.cols} onChange={v => patchParams('dup', { cols: v })} columns={columns} /></div>
        )}
        {spec.template === 'firstLast' && (
          <>
            <div className="dan-field"><span>{t('common:dan.tplCols.firstLast')}</span><ColumnChips value={tp.firstLast.keys} onChange={v => patchParams('firstLast', { keys: v })} columns={columns} /></div>
            <div className="dan-field"><span>{t('common:dan.orderCol')}</span><ColumnSelect value={tp.firstLast.orderCol} onChange={v => patchParams('firstLast', { orderCol: v })} columns={columns} /></div>
            <div className="dan-field"><span>{t('common:dan.which')}</span>
              <select value={tp.firstLast.which} onChange={e => patchParams('firstLast', { which: e.target.value })}>
                <option value="last">{t('common:dan.whichLast')}</option>
                <option value="first">{t('common:dan.whichFirst')}</option>
              </select>
            </div>
          </>
        )}
      </div>
      <div className="dan-note">{t('common:dan.tplFilterNote')}</div>
    </div>
  );

  const renderTab = () => {
    if (spec.source.kind === 'table' && colState.loading) return <div className="dan-empty">{t('common:dan.loadingColumns')}</div>;
    if (spec.source.kind === 'table' && colState.error) return <div className="dan-error">{colState.error}</div>;
    if (!columns.length) return <div className="dan-empty">{t(spec.source.kind === 'table' ? 'common:dan.pickTableFirst' : 'common:dan.noResult')}</div>;
    switch (activeTab) {
      case 'template': return renderTemplate();
      case 'filter': return renderFilter();
      case 'group': return renderGroup();
      case 'sort': return renderSort();
      case 'analytic': return renderAnalytic();
      case 'pivot': return renderPivot();
      default: return renderColumns();
    }
  };

  const src = spec.source;
  return (
    <AppModal windowed open={open} onClose={onClose} title={t('menu:edit.analysis')} icon={<FiPieChart size={14} />} className="app-modal-default dan-modal"
      titleExtra={<EditorConnectionChip connections={connections} value={connectionId} />}>
      {/* 왼쪽 = 저장한 분석 목록, 오른쪽 = 구성 — SQL 작성·튜닝과 같은 틀(2026-10-28 사용자 요청, common/Workflow.css 의 stn-side) */}
      <div className="stn-body dan-shell">
      <aside className="stn-side">
        <button type="button" className="app-btn app-btn-primary stn-full" onClick={startNew}><FiPlus size={12} /> {t('common:dan.newDef')}</button>
        <input className="stn-full" value={defQ} onChange={e => setDefQ(e.target.value)} placeholder={t('common:dan.defSearch')} />
        <ul className="stn-jobs">
          {visibleDefs.map(d => (
            <li key={d.id} className={cur && cur.id === d.id ? 'on' : ''} onClick={() => loadDef(d.id)}>
              <div className="stn-job-title">{d.name}</div>
              <div className="stn-job-meta">{t(`common:dan.tpl.${d.template}`, { defaultValue: d.template })}{d.target ? ` · ${d.target === 'result' ? t('common:dan.kind.result') : d.target}` : ''} · {storedTime(d.updatedAt, 'date')}</div>
            </li>
          ))}
          {!visibleDefs.length && <li className="stn-empty">{defs.length ? t('common:dan.noDefMatch') : t('common:dan.noDefs')}</li>}
        </ul>
      </aside>
      <div className="dan-main">
      <div className="dan-body" ref={bodyRef}>
        <section className="dan-left" style={{ width: leftWidth }}>
          <div className="dan-head">
            <div className="dan-row">
              <span className="dan-label">{t('common:dan.target')}</span>
              <div className="dan-seg" role="radiogroup" aria-label={t('common:dan.target')}>
                {['table', 'result'].map(k => (
                  <button key={k} type="button" role="radio" aria-checked={src.kind === k} className={src.kind === k ? 'on' : ''} onClick={() => switchKind(k)}>{t(`common:dan.kind.${k}`)}</button>
                ))}
              </div>
              {src.kind === 'table' && src.table && (
                <span className="dan-target" title={colState.comment || src.table}><b className="mono">{src.table}</b>{colState.comment ? <span className="dan-cmt"> {colState.comment}</span> : null}</span>
              )}
              {src.kind === 'result' && (
                <>
                  <span className="dan-target" title={src.sql || ''}>{src.resultName || t('common:dan.resultQuery')}</span>
                  <button type="button" className="app-btn dan-push" onClick={readResult} title={t('common:dan.rereadHint')}><FiRefreshCw size={12} /> {t('common:dan.reread')}</button>
                </>
              )}
            </div>
            {src.kind === 'table' && (
              <TablePicker connectionId={connectionId} schema={src.schema} table={src.table}
                onSchema={v => patchSource({ schema: v })} onPick={v => patchSource({ table: v })} />
            )}
            <div className="dan-row">
              <span className="dan-label">{t('common:dan.template')}</span>
              <select value={spec.template} onChange={(e) => { patch({ template: e.target.value }); setTab(e.target.value === 'custom' ? 'columns' : 'template'); }}>
                {TEMPLATES.map(k => <option key={k} value={k}>{t(`common:dan.tpl.${k}`)}</option>)}
              </select>
              <span className="dan-label">{t('common:ap.rowLimit')}</span>
              <select value={spec.rowLimit || 0} onChange={e => patch({ rowLimit: Number(e.target.value) || 0 })} disabled={spec.template === 'unique' || spec.template === 'topn'}
                title={spec.template === 'topn' ? t('common:dan.topnLimitNote') : undefined}>
                {ROW_CHOICES.map(n => <option key={n} value={n}>{n ? t('common:dan.rows', { count: n }) : t('common:ap.unlimited')}</option>)}
              </select>
              <label className="dan-check" title={hints ? t('common:dan.parallelHint') : t('common:dan.parallelNone')}>
                <input type="checkbox" checked={!!spec.parallel.on && hints} disabled={!hints} onChange={e => patch({ parallel: { ...spec.parallel, on: e.target.checked } })} /> PARALLEL
              </label>
              <input type="number" min={2} max={512} className="dan-degree" value={spec.parallel.degree || ''} disabled={!hints || !spec.parallel.on}
                onChange={e => patch({ parallel: { ...spec.parallel, degree: e.target.value } })} title={t('common:dan.degree')} aria-label={t('common:dan.degree')} />
            </div>
          </div>
          <div className="dan-tabs" role="tablist">
            {tabs.map(k => (
              <button key={k} type="button" role="tab" aria-selected={activeTab === k} className={activeTab === k ? 'on' : ''} onClick={() => setTab(k)}>
                {t(`common:dan.tabs.${k}`)}
                {k === 'analytic' && spec.analytics.length ? <span className="dan-badge">{spec.analytics.length}</span> : null}
                {k === 'pivot' && pv.mode !== 'none' ? <span className="dan-badge">{pv.mode.toUpperCase()}</span> : null}
              </button>
            ))}
          </div>
          <div className="dan-scroll" key={activeTab}>{renderTab()}</div>
        </section>
        <div className={`dan-split${splitDrag ? ' dragging' : ''}`} role="separator" aria-orientation="vertical"
          aria-label={t('common:dan.resize')} title={t('common:dan.resize')} onMouseDown={startSplit} />
        <section className="dan-right">
          <div className="dan-right-bar">
            <span className="dan-right-title">{t('common:dan.preview')}</span>
            <button type="button" className="app-btn" onClick={copy} disabled={!built.sql}><FiCopy size={12} /> {t('common:dan.copy')}</button>
          </div>
          <div className="dan-editor">
            <SQLEditor query={built.sql} setQuery={NOOP} readOnly minimap={false} autocomplete={false} dbType={db}
              onExecute={NOOP} onExecutePlan={NOOP} onViewSchemaInfoByEditor={NOOP} />
          </div>
          {(built.error || built.hint) && <div className={built.error ? 'dan-error' : 'dan-hintbox'} role="status">{built.error || built.hint}</div>}
        </section>
      </div>
      <footer className="dan-foot">
        <span className="dan-cur" title={cur ? cur.name : undefined}>{cur ? cur.name : t('common:dan.unsavedDef')}</span>
        <button type="button" className="app-btn" onClick={saveDef} disabled={cur ? !cur.writable : false}><FiSave size={12} /> {t('common:action.save')}</button>
        <button type="button" className="app-btn" onClick={removeDef} disabled={!cur}><FiTrash2 size={12} /> {t('common:ui.delete')}</button>
        <span className="dan-foot-gap" />
        <button type="button" className="app-btn" onClick={() => onNewTab && onNewTab(built.sql)} disabled={!built.sql}><FiFilePlus size={12} /> {t('common:dan.newTab')}</button>
        <button type="button" className="app-btn app-btn-primary" onClick={() => onInsert && onInsert(built.sql)} disabled={!built.sql} title={t('common:dan.insertHint')}>
          <FiUpload size={12} /> {t('common:dan.insert')}
        </button>
      </footer>
      </div>
      </div>
    </AppModal>
  );
}

DataAnalysisModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  connections: PropTypes.arrayOf(PropTypes.object),
  connectionId: PropTypes.string,
  dbType: PropTypes.string,
  /** 지금 결과 탭 — { sql, name, columns(메타), sampleRow } */
  resultSource: PropTypes.object,
  onInsert: PropTypes.func,
  onNewTab: PropTypes.func,
};

// 편집기에 글자를 칠 때마다 App 이 다시 그려져도 값이 같으면 다시 그리지 않는다(common/memoWindow, 2026-10-26 — 입력 지연)
export default memoWindow(DataAnalysisModal);
